/**
 * Routing, and the gate in front of it.
 *
 * **Navigation is driven by session state, not by screens.** No screen calls
 * `navigate()` after signing in; `SessionProvider` resolves auth plus claims
 * into a `SessionStage`, and this file turns that into what renders. It is the
 * same shape as `route_guard.dart` in the Flutter app, and it is what lets a
 * forced token refresh — a freshly granted admin claim — move the user without
 * any screen knowing it happened.
 *
 * `notAdmin` gets its own screen rather than being folded into `signedOut`.
 * They are different facts: one person needs to sign in, the other is signed
 * in and needs provisioning. Showing "please sign in" to someone already
 * signed in is how a support call starts.
 */
import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, NavLink } from 'react-router-dom'
import { SessionProvider, useSession } from './lib/auth'
import SignIn from './features/SignIn'
import {
  IconBell,
  IconBroadcast,
  IconCategories,
  IconDashboard,
  IconImport,
  IconInventory,
  IconOrders,
  IconPartners,
  IconProducts,
  IconPromotions,
  IconReports,
  IconRetailers,
  IconSettings,
  IconSignOut,
} from './components/icons'

const Dashboard = lazy(() => import('./features/Dashboard'))
const Orders = lazy(() => import('./features/Orders'))
const OrderDetail = lazy(() => import('./features/OrderDetail'))
const Products = lazy(() => import('./features/Products'))
const ProductEdit = lazy(() => import('./features/ProductEdit'))
const Categories = lazy(() => import('./features/Categories'))
const Inventory = lazy(() => import('./features/Inventory'))
const BulkImport = lazy(() => import('./features/BulkImport'))
const Retailers = lazy(() => import('./features/Retailers'))
const RetailerDetail = lazy(() => import('./features/RetailerDetail'))
const Promotions = lazy(() => import('./features/Promotions'))
const Partners = lazy(() => import('./features/Partners'))
const Reports = lazy(() => import('./features/Reports'))
const Broadcast = lazy(() => import('./features/Broadcast'))
const Notifications = lazy(() => import('./features/Notifications'))
const Settings = lazy(() => import('./features/Settings'))
const Admins = lazy(() => import('./features/Admins'))

function Sidebar() {
  const { user, signOut, isSuperAdmin } = useSession()
  const link = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'nav-link active' : 'nav-link'

  // The last four digits are what an admin recognises as "their" number, and
  // the full one is too long for a 244px rail.
  const phone = user?.phoneNumber ?? ''
  const initial = phone.slice(-2, -1) || 'A'

  return (
    <nav className="nav">
      <div className="nav-brand">
        <span className="nav-mark" aria-hidden="true">
          J
        </span>
        <span>
          Jaagur
          <small>Admin console</small>
        </span>
      </div>

      <NavLink to="/" end className={link}>
        <IconDashboard />
        <span>Dashboard</span>
      </NavLink>
      <NavLink to="/orders" className={link}>
        <IconOrders />
        <span>Orders</span>
      </NavLink>

      <div className="nav-section">Catalogue</div>
      <NavLink to="/products" className={link}>
        <IconProducts />
        <span>Products</span>
      </NavLink>
      <NavLink to="/categories" className={link}>
        <IconCategories />
        <span>Categories</span>
      </NavLink>
      <NavLink to="/inventory" className={link}>
        <IconInventory />
        <span>Inventory</span>
      </NavLink>
      <NavLink to="/import" className={link}>
        <IconImport />
        <span>Bulk import</span>
      </NavLink>

      <div className="nav-section">People</div>
      <NavLink to="/retailers" className={link}>
        <IconRetailers />
        <span>Retailers</span>
      </NavLink>
      <NavLink to="/partners" className={link}>
        <IconPartners />
        <span>Delivery partners</span>
      </NavLink>

      <div className="nav-section">Growth</div>
      <NavLink to="/promotions" className={link}>
        <IconPromotions />
        <span>Offers &amp; coupons</span>
      </NavLink>
      <NavLink to="/reports" className={link}>
        <IconReports />
        <span>Reports</span>
      </NavLink>
      <NavLink to="/broadcast" className={link}>
        <IconBroadcast />
        <span>Broadcast</span>
      </NavLink>
      <NavLink to="/notifications" className={link}>
        <IconBell />
        <span>Notifications</span>
      </NavLink>

      <div className="nav-section">Admin</div>
      {/* Super admins only. The route is registered unconditionally below —
          this hides the way in, `setUserRole` is what refuses the write. */}
      {isSuperAdmin && (
        <NavLink to="/admins" className={link}>
          <IconRetailers />
          <span>Admins</span>
        </NavLink>
      )}
      <NavLink to="/settings" className={link}>
        <IconSettings />
        <span>Settings</span>
      </NavLink>

      <div className="nav-foot">
        <div className="nav-user">
          <span className="nav-avatar" aria-hidden="true">
            {initial}
          </span>
          <span className="nav-user-meta">
            <b className="mono">{phone || '—'}</b>
            {/* The tier, not a generic word. A super admin reading
                "Administrator" has no way to tell from the console whether
                the grant landed — which is exactly the question asked after
                running the provisioning script. */}
            <span>{isSuperAdmin ? 'Super admin' : 'Administrator'}</span>
          </span>
        </div>
        <button
          className="btn btn-sm"
          onClick={() => void signOut()}
          title="Sign out"
        >
          <IconSignOut />
          <span>Sign out</span>
        </button>
      </div>
    </nav>
  )
}

function Shell() {
  return (
    <div className="app-shell">
      <Sidebar />
      <main>
        <Suspense
          fallback={
            <div className="page">
              <div className="skeleton" style={{ height: 28, width: 220, marginBottom: 20 }} />
              <div className="skeleton" style={{ height: 220 }} />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/:orderId" element={<OrderDetail />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/new" element={<ProductEdit />} />
            <Route path="/products/:productId" element={<ProductEdit />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/import" element={<BulkImport />} />
            <Route path="/retailers" element={<Retailers />} />
            <Route path="/retailers/:shopId" element={<RetailerDetail />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/promotions" element={<Promotions />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/broadcast" element={<Broadcast />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/admins" element={<Admins />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  )
}

function NotAdmin() {
  const { user, signOut } = useSession()
  return (
    <div className="signin-wrap">
      <div className="signin-card">
        <h1 style={{ marginBottom: 'var(--sp-3)' }}>Not an admin account</h1>
        <p style={{ color: 'var(--ink-soft)', marginBottom: 'var(--sp-5)' }}>
          {user?.phoneNumber} is signed in, but does not hold the admin role.
          Admin is granted by running{' '}
          <code className="mono">scripts/provision-admin.mjs</code> against this
          number — it cannot be granted from inside this console, by design.
        </p>
        <button className="btn" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </div>
  )
}

function Gate() {
  const { stage } = useSession()

  switch (stage) {
    case 'loading':
      return (
        <div className="signin-wrap">
          <div className="skeleton" style={{ height: 18, width: 160 }} />
        </div>
      )
    case 'signedOut':
      return <SignIn />
    case 'notAdmin':
      return <NotAdmin />
    case 'admin':
      return <Shell />
  }
}

export default function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Gate />
      </BrowserRouter>
    </SessionProvider>
  )
}
